import { Badge } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";

export interface UserRow {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  role: "buyer" | "breeder" | "admin" | null;
  city: string | null;
  status: "active" | "suspended" | "banned";
  trust_score: number;
  risk_score: number;
  is_onboarded: boolean;
  created_at: string;
  last_seen_at: string | null;
}

export const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { variant: "success" | "warning" | "destructive" | "secondary"; label: string }> = {
    active: { variant: "success", label: "Active" },
    suspended: { variant: "warning", label: "Suspended" },
    banned: { variant: "destructive", label: "Banned" },
  };
  const cfg = map[status] ?? { variant: "secondary" as const, label: status };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
};

export const RoleBadge = ({ role }: { role: string | null }) => {
  if (!role) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant={role === "breeder" ? "default" : "secondary"}>
      {role === "breeder" ? "Breeder" : role === "buyer" ? "Buyer" : "Admin"}
    </Badge>
  );
};

/**
 * Score pill. Trust and risk read in opposite directions, so the colour scale
 * is inverted between them — a high risk score is bad, a high trust score is
 * good, and an admin scanning a list should not have to remember which.
 */
export const ScorePill = ({
  score,
  kind,
}: {
  score: number;
  kind: "trust" | "risk";
}) => {
  const tone =
    kind === "trust"
      ? score >= 70
        ? "text-success"
        : score >= 40
          ? "text-warning"
          : "text-destructive"
      : score >= 70
        ? "text-destructive"
        : score >= 40
          ? "text-warning"
          : "text-muted-foreground";

  return (
    <span
      className={cn("numeric text-sm font-medium", tone)}
      title={`${kind === "trust" ? "Trust" : "Risk"} score ${score} of 100`}
    >
      {score}
    </span>
  );
};

export const LISTING_STATUS_BADGE: Record<
  string,
  { variant: "success" | "warning" | "destructive" | "secondary"; label: string }
> = {
  available: { variant: "success", label: "Live" },
  sold: { variant: "secondary", label: "Sold" },
  removed: { variant: "destructive", label: "Removed" },
  flagged: { variant: "warning", label: "Flagged" },
  draft: { variant: "secondary", label: "Draft" },
};
